import { useEffect, useRef, useState } from "react";
import HomeManager from "../managers/HomeManager";
import { useSiteContext } from "../SiteContext";
import { peekStashedDeepLink } from "../interfaces/DeepLinkInterface";
import { PayPeriod } from "../interfaces/PayPeriodInterface";

// Convert date string to mm/dd/yyyy format
export const getPayPeriodDate = (date: Date): string => {
  // Don't adjust for time zone offset since pay period dates are already at 0:00:00 time
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month.toString().padStart(2, "0")}/${day.toString().padStart(2, "0")}/${year}`;
};

/**
 * Pay-periods fetch + current-week auto-pick + startDate/endDate sync, extracted from
 * WeekSelector.tsx so the logic can be single-sourced (also consumed by LayoutV2).
 * Uses SiteContext internally; safe to call from a component that renders only when logged in.
 * Returns the loaded pay periods and any fetch error (WeekSelector still renders its own selects).
 */
export function usePayPeriods(): { payPeriods: PayPeriod[]; error: string | null } {
  const {
    payPeriods,
    setPayPeriods,
    setWeek,
    setYear,
    week,
    year,
    setPayPeriodID,
    setEndDate,
    setStartDate,
    setLoading, // Use loading and setLoading from SiteContext
    pendingDeepLink,
  } = useSiteContext();

  const [error, setError] = useState<string | null>(null);

  // The fetch below resolves asynchronously, and the "is a week already selected?"
  // check has to see the value at THAT moment, not the one captured when the effect
  // was created. A page that selects a period while the list is loading (the misc
  // task deep link, TAS-544) would otherwise be clobbered by the current-week
  // default the instant the fetch returned — setPayPeriods() re-renders the page,
  // the page picks its week, and the stale closure here still reads week === 0.
  const weekRef = useRef(week);
  const yearRef = useRef(year);
  weekRef.current = week;
  yearRef.current = year;
  // The deep link is read the same way: at resolve time, not at effect creation.
  const deepLinkRef = useRef(pendingDeepLink);
  deepLinkRef.current = pendingDeepLink;

  // Fetch pay periods when component mounts
  useEffect(() => {
    const getPayPeriods = async () => {
      try {
        setLoading(true); // Start loading
        const response: PayPeriod[] = await HomeManager.fetchPayPeriods();
        setPayPeriods(response);

        // Only proceed to find the current pay period if week and year are not set
        // — read through the refs so a selection made during the fetch is respected.
        if (weekRef.current === 0 && yearRef.current === "") {
          // A deep link naming a pay period (`?payPeriodID=N`, e.g. the daily reds
          // emails, TAS-544) wins over "the week containing today". Decided HERE,
          // in the same place the default is chosen, because the three setters
          // below are not atomic: a page that selects its own week while this
          // continuation runs gets overwritten by whichever setter runs last.
          const wantedPayPeriodID =
            deepLinkRef.current?.payPeriodID ?? peekStashedDeepLink()?.payPeriodID;
          const linkedPayPeriod = wantedPayPeriodID
            ? response.find((period) => period.PayPeriodID === wantedPayPeriodID)
            : undefined;
          if (linkedPayPeriod) {
            setWeek(linkedPayPeriod.WeekNumber || 1);
            setYear(linkedPayPeriod.AccountingYear.toLocaleString() || "2025");
            setPayPeriodID(linkedPayPeriod.PayPeriodID);
            console.log("Deep-linked Pay Period:", linkedPayPeriod);
            return;
          }

          // Get the current date
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);


          // Find the pay period that contains the current date (between start and end dates, inclusive)
          let currentPayPeriod: PayPeriod | null = null;

          for (const period of response) {
            const startDate = new Date(period.StartDate);
            const endDate = new Date(period.EndDate);

            // Adjust endDate by adding one day to make the range inclusive
            endDate.setDate(endDate.getDate() + 1);
            console.log("Start Date:", startDate);
            console.log("End Date:", endDate);
            console.log("Current Date:", currentDate);

            // Check if current date is between start and end dates (inclusive)
            // Since pay period dates are at 0:00:00 time, we don't need to convert locally
            if (currentDate >= startDate && currentDate <= endDate) {
              currentPayPeriod = period;
              break; // Found the current pay period, no need to continue
            }
          }

          if (currentPayPeriod) {
            setWeek(currentPayPeriod.WeekNumber || 1); // Default to week 1 if WeekNumber is invalid
            setYear(currentPayPeriod.AccountingYear.toLocaleString() || "2025"); // Default to 2025 if AccountingYear is invalid
            setPayPeriodID(currentPayPeriod.PayPeriodID); // Set initial PayPeriodID

            // Debug: Log current pay period
            console.log("Current Pay Period:", currentPayPeriod);
          } else {
            console.log("No current pay period found, using first available period.");
            if (response.length > 0) {
              const firstPayPeriod = response[0];
              setWeek(firstPayPeriod.WeekNumber || 1);
              setYear(firstPayPeriod.AccountingYear.toLocaleString() || "2024");
              setPayPeriodID(firstPayPeriod.PayPeriodID);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching pay periods:", error);
        setError("Failed to fetch pay periods. Please try again later.");
      } finally {
        setLoading(false); // Stop loading after data fetch
      }
    };

    getPayPeriods();
  }, [week, year]); // Add week and year to dependencies

  // Update start and end dates from selected week/year and set the PayPeriodID
  const updateDatesFromPayPeriod = (week: number, year: string) => {
    const payPeriod = payPeriods.find(
      (period) => period.WeekNumber === week && period.AccountingYear.toLocaleString() === year
    );
    if (payPeriod) {
      const startDate = getPayPeriodDate(new Date(payPeriod.StartDate));
      const endDate = getPayPeriodDate(new Date(payPeriod.EndDate));
      setStartDate(startDate);
      setEndDate(endDate);
      setPayPeriodID(payPeriod.PayPeriodID); // Update PayPeriodID when week/year changes

      // Debug: Log updated pay period
      console.log("Updated Pay Period:", payPeriod);
    }
  };

  // Filter pay periods based on selected year
  const filteredPayPeriods = payPeriods
    .filter((period) => {
      if (!period || !period.AccountingYear) return false;
      return period.AccountingYear.toLocaleString() === year && period.WeekNumber > 0;
    });

  // Effect to update the dates and PayPeriodID when week or year changes
  useEffect(() => {
    if (filteredPayPeriods.length > 0) {
      updateDatesFromPayPeriod(week, year);
    }
  }, [week, year, setPayPeriodID]);

  return { payPeriods, error };
}

export default usePayPeriods;
